import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useGetLabelGroupListForWorkspace } from "@/domain/label/hooks/api/use-get-label-group-list-for-workspace";
import { useGetLabelListForWorkspace } from "@/domain/label/hooks/api/use-get-label-list-for-workspace";

import { DataRequestLabelDialog } from "./data-request-label-dialog";
import { SelectFieldProps } from "./select-field-props";

export function LabelSelectField({ workspaceId, value, onChange, disabled }: SelectFieldProps) {
  const { t } = useDomainTranslation("data-request");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { labelList } = useGetLabelListForWorkspace({ workspaceId });
  const { labelGroupList } = useGetLabelGroupListForWorkspace({ workspaceId });

  const selectedIdSet = useMemo(() => {
    if (!value) {
      return new Set<string>();
    }
    return new Set(value.split(",").filter(Boolean));
  }, [value]);

  const selectedLabelList = useMemo(() => {
    return labelList.filter((l) => selectedIdSet.has(l.id));
  }, [labelList, selectedIdSet]);

  const labelGroupMap = useMemo(() => new Map(labelGroupList.map((g) => [g.id, g.title])), [labelGroupList]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-auto min-h-8 w-full justify-between text-sm font-normal"
        disabled={disabled}
        onClick={() => setIsDialogOpen(true)}
        aria-label={t("dataEntry.fieldTable.selectLabels")}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1 overflow-hidden">
          {selectedLabelList.length > 0
            ? selectedLabelList.map((label) => (
                <span
                  key={label.id}
                  className="flex items-center gap-1 rounded-md border-[0.5px] bg-background px-1.5 py-0.5 text-xs"
                >
                  {label.color && (
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: label.color }} />
                  )}
                  <span>
                    {label.labelGroupId && labelGroupMap.get(label.labelGroupId)
                      ? `${labelGroupMap.get(label.labelGroupId)}: ${label.title}`
                      : label.title}
                  </span>
                </span>
              ))
            : null}
        </span>
        <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
      </Button>
      <DataRequestLabelDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        workspaceId={workspaceId}
        value={value}
        onChange={onChange}
      />
    </>
  );
}
